import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Mail, User as UserIcon, Reply, PenLine, Lock, KeyRound, Eye, EyeOff } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BrandKitManager } from '../components/settings/BrandKitManager';
import { TeamManager } from '../components/settings/TeamManager';

export default function Settings() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [footer, setFooter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Password change
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    const run = () => {
      api.get('/auth/me')
        .then((r) => {
          setName(r.data?.name || '');
          setReplyTo(r.data?.reply_to_email || '');
          setFooter(r.data?.email_footer || '');
          updateUser(r.data);
        })
        .catch(() => {
          // Fall back to context user if the call fails
          setName(user?.name || '');
          setReplyTo(user?.reply_to_email || '');
          setFooter(user?.email_footer || '');
        })
        .finally(() => setLoading(false));
    };
    const t = setTimeout(run, 0);
    return () => clearTimeout(t);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch('/auth/me', {
        name: name.trim(),
        reply_to_email: replyTo.trim(),
        email_footer: footer.trim(),
      });
      updateUser(r.data);
      setName(r.data?.name || '');
      setReplyTo(r.data?.reply_to_email || '');
      setFooter(r.data?.email_footer || '');
      toast.success('Settings saved');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not save settings. Please try again.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!currentPw || !newPw || !confirmPw) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (newPw.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPw !== confirmPw) {
      toast.error('New password and confirmation do not match');
      return;
    }
    if (newPw === currentPw) {
      toast.error('New password must be different from the current one');
      return;
    }
    setChangingPw(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPw,
        new_password: newPw,
      });
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      toast.success('Password updated successfully');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not change password. Please try again.';
      toast.error(msg);
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div data-testid="settings-page">
        <PageHeader
          title="Settings"
          subtitle="Manage your host profile and how guests reach you."
        />

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" data-testid="settings-loading" />
          </div>
        ) : (
          <div className="max-w-2xl space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <UserIcon className="h-[18px] w-[18px] text-primary" /> Host profile
                </CardTitle>
                <CardDescription>This name appears as the sender on your invitations.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="host-name">Display name</Label>
                  <Input
                    id="host-name"
                    data-testid="settings-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name or organization"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="host-email" className="flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" /> Account email
                  </Label>
                  <Input id="host-email" value={user?.email || ''} disabled data-testid="settings-account-email" />
                  <p className="text-xs text-muted-foreground">Your login email. Contact support to change it.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Reply className="h-[18px] w-[18px] text-primary" /> Email replies
                </CardTitle>
                <CardDescription>
                  When a guest hits &quot;Reply&quot; on an invitation, their message goes here.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reply-to">Reply-to email</Label>
                  <Input
                    id="reply-to"
                    type="email"
                    data-testid="settings-replyto-input"
                    value={replyTo}
                    onChange={(e) => setReplyTo(e.target.value)}
                    placeholder="you@example.com"
                  />
                  <p className="text-xs text-muted-foreground" data-testid="settings-replyto-hint">
                    {replyTo.trim()
                      ? `Replies to your invites will be delivered to ${replyTo.trim()}.`
                      : 'Leave blank to keep replies going to the default sending address (not monitored).'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <PenLine className="h-[18px] w-[18px] text-primary" /> Email footer
                </CardTitle>
                <CardDescription>
                  The small line shown at the bottom of every invitation, reminder and follow-up email.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email-footer">Footer text</Label>
                  <Input
                    id="email-footer"
                    maxLength={160}
                    data-testid="settings-footer-input"
                    value={footer}
                    onChange={(e) => setFooter(e.target.value)}
                    placeholder="Crafted by Thrive Gatherings · thrivegathering.life"
                  />
                  <p className="text-xs text-muted-foreground" data-testid="settings-footer-hint">
                    {footer.trim()
                      ? 'This text will appear at the bottom of your emails (the unsubscribe link is always added automatically).'
                      : 'Leave blank to use the default: “Crafted by Thrive Gatherings · thrivegathering.life”.'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} className="gap-2 h-11" data-testid="settings-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save changes
              </Button>
            </div>

            <Card data-testid="settings-password-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Lock className="h-[18px] w-[18px] text-primary" /> Password
                </CardTitle>
                <CardDescription>
                  Update the password used to sign in to your host account.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current-pw">Current password</Label>
                  <div className="relative">
                    <Input
                      id="current-pw"
                      type={showPw ? 'text' : 'password'}
                      autoComplete="current-password"
                      data-testid="settings-current-password"
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      placeholder="Enter current password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPw ? 'Hide passwords' : 'Show passwords'}
                      data-testid="settings-toggle-password-visibility"
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-pw">New password</Label>
                  <Input
                    id="new-pw"
                    type={showPw ? 'text' : 'password'}
                    autoComplete="new-password"
                    data-testid="settings-new-password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="At least 8 characters"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-pw">Confirm new password</Label>
                  <Input
                    id="confirm-pw"
                    type={showPw ? 'text' : 'password'}
                    autoComplete="new-password"
                    data-testid="settings-confirm-password"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder="Re-enter new password"
                  />
                  {confirmPw && newPw !== confirmPw && (
                    <p className="text-xs text-destructive" data-testid="settings-password-mismatch">
                      Passwords do not match.
                    </p>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={changePassword}
                    disabled={changingPw}
                    variant="outline"
                    className="gap-2 h-11"
                    data-testid="settings-change-password-button"
                  >
                    {changingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                    Update password
                  </Button>
                </div>
              </CardContent>
            </Card>

            <BrandKitManager />

            <TeamManager />
          </div>
        )}
    </div>
  );
}
